import { makeStyles } from "@material-ui/core";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import NButton from "@View/components/ui/NButton";

const useStyles = makeStyles((_theme) => ({
  results: {
    marginTop: "20px",
    boxSizing: "border-box",
  },
  resultsHeader: {
    marginBottom: "12px",
  },
  resultTitle: {
    fontWeight: 600,
    color: "#1A1E24",
    marginBottom: "10px",
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "14px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#54606F",
    backgroundColor: "#fff",
    border: "1px solid #e9ecef",
    cursor: "pointer",
    userSelect: "none",
    transition: "all .15s ease",
    "&:hover": {
      borderColor: "#A1ADBA",
    },
  },
  chipActive: {
    borderColor: "#6FA4F2",
    backgroundColor: "#eef4fe",
    color: "#1A1E24",
  },
  chipDot: {
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
  },
  errorMessage: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#dc3545",
    marginBottom: "12px",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "6px 10px",
    marginBottom: "10px",
    fontSize: "13px",
    color: "#1A1E24",
    border: "1px solid #e9ecef",
    borderRadius: "8px",
    outline: "none",
    "&:focus": {
      borderColor: "#6FA4F2",
    },
  },
  tableWrap: {
    maxHeight: "45vh",
    overflowY: "auto",
    boxSizing: "border-box",
  },
  resultsTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "12px",
    tableLayout: "fixed",
    "& thead th": {
      position: "sticky",
      top: 0,
      zIndex: 1,
      backgroundColor: "#fff",
      // Граница рисуется тенью, чтобы «залипала» вместе с ячейкой,
      // а белая линия сверху закрывает 1px-просвет при прокрутке.
      boxShadow: "0 -1px 0 #fff, inset 0 -1px 0 #e9ecef",
    },
    "& th": {
      textAlign: "left",
      fontWeight: 600,
      fontSize: "11px",
      color: "#A1ADBA",
      padding: "4px 8px",
    },
    "& td": {
      padding: "5px 8px",
      verticalAlign: "middle",
      borderBottom: "1px solid #e9ecef",
    },
    "& tr:last-child td": {
      borderBottom: "none",
    },
  },
  thStatus: {
    width: "28px",
  },
  rowOk: {
    backgroundColor: "#fff",
  },
  rowError: {
    backgroundColor: "#fdf2f3",
  },
  rowEmpty: {
    backgroundColor: "#fff8ec",
  },
  tdStatus: {
    width: "28px",
  },
  statusIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    fontSize: "10px",
    fontWeight: 700,
    lineHeight: 1,
    color: "#fff",
  },
  statusIconOk: {
    backgroundColor: "#28a745",
  },
  statusIconError: {
    backgroundColor: "#dc3545",
  },
  statusIconEmpty: {
    backgroundColor: "#f0ad4e",
  },
  tdName: {
    fontWeight: 600,
    color: "#1A1E24",
    wordBreak: "break-all",
  },
  tdType: {
    width: "28%",
    color: "#54606F",
  },
  tdError: {
    width: "35%",
    color: "#dc3545",
    wordBreak: "break-word",
  },
  tdNote: {
    width: "35%",
    color: "#c77d11",
    wordBreak: "break-word",
  },
  notFound: {
    padding: "20px",
    textAlign: "center",
    color: "#A1ADBA",
    fontSize: "13px",
  },
  closeButton: {
    marginTop: "15px",
    marginBottom: "-10px",
    textAlign: "center",
  },
}));

export interface UploadResultFile {
  fileName: string;
  type?: string;
  error?: string;
  isEmpty?: boolean;
}

export interface UploadResultData {
  processedFiles?: number;
  details?: {
    successful?: UploadResultFile[];
    failed?: UploadResultFile[];
  };
}

interface UploadResultsViewProps {
  resultData: UploadResultData | null;
  errorMessage?: string | null;
  onClose: () => void;
}

type FilterKey = "all" | "ok" | "empty" | "failed";

// Порог, после которого показываем поле поиска по имени файла.
const SEARCH_THRESHOLD = 10;

const UploadResultsView: React.FC<UploadResultsViewProps> = ({
  resultData,
  errorMessage,
  onClose,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [filter, setFilter] = useState<FilterKey>("all");
  const [search, setSearch] = useState("");

  const successfulFiles = resultData?.details?.successful ?? [];
  const failedFiles = resultData?.details?.failed ?? [];
  const hasResults = !!resultData?.details;
  const totalFiles =
    resultData?.processedFiles ?? successfulFiles.length + failedFiles.length;

  const emptyCount = successfulFiles.filter((f) => f.isEmpty).length;
  const okCount = successfulFiles.length - emptyCount;
  const failedCount = failedFiles.length;

  // Единый список: сначала ошибки, затем пустые, затем успешные.
  const categorize = (file: UploadResultFile, isOk: boolean): FilterKey =>
    !isOk ? "failed" : file.isEmpty ? "empty" : "ok";

  const tableFiles = [
    ...failedFiles.map((file) => ({ ...file, isOk: false, category: categorize(file, false) })),
    ...successfulFiles
      .filter((f) => f.isEmpty)
      .map((file) => ({ ...file, isOk: true, category: "empty" as FilterKey })),
    ...successfulFiles
      .filter((f) => !f.isEmpty)
      .map((file) => ({ ...file, isOk: true, category: "ok" as FilterKey })),
  ];

  const searchTerm = search.trim().toLowerCase();
  const filteredFiles = tableFiles.filter((file) => {
    if (filter !== "all" && file.category !== filter) return false;
    if (
      searchTerm &&
      !file.fileName?.toLowerCase().includes(searchTerm) &&
      !file.type?.toLowerCase().includes(searchTerm)
    )
      return false;
    return true;
  });

  const renderChip = (key: FilterKey, label: string, count: number, color: string) => (
    <button
      type="button"
      className={`${classes.chip} ${filter === key ? classes.chipActive : ""}`}
      onClick={() => setFilter(key)}
    >
      <span className={classes.chipDot} style={{ backgroundColor: color }} />
      {label}: {count}
    </button>
  );

  return (
    <>
      <div className={classes.results}>
        <div className={classes.resultsHeader}>
          <div className={classes.resultTitle}>
            {errorMessage
              ? t("bankStatement.uploadError")
              : t("bankStatement.uploadResults")}
          </div>
          {hasResults && (
            <div className={classes.filters}>
              {renderChip("all", t("bankStatement.all"), totalFiles, "#6FA4F2")}
              {okCount > 0 &&
                renderChip("ok", t("bankStatement.successful"), okCount, "#28a745")}
              {emptyCount > 0 &&
                renderChip("empty", t("bankStatement.empty"), emptyCount, "#f0ad4e")}
              {failedCount > 0 &&
                renderChip("failed", t("bankStatement.failed"), failedCount, "#dc3545")}
            </div>
          )}
        </div>

        {/* Ошибка WebSocket, если есть */}
        {errorMessage && <div className={classes.errorMessage}>{errorMessage}</div>}

        {/* Поиск по имени файла — только для больших списков */}
        {tableFiles.length > SEARCH_THRESHOLD && (
          <input
            className={classes.searchInput}
            placeholder={t("bankStatement.input.placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}

        {/* Таблица результатов. Шапка со сводкой и поиск остаются на месте,
            скроллится только таблица. */}
        {tableFiles.length > 0 && (
          <div className={classes.tableWrap}>
            <table className={classes.resultsTable}>
              <thead>
                <tr>
                  <th className={classes.thStatus} />
                  <th>{t("attach.docName")}</th>
                  <th>{t("bankStatement.type")}</th>
                  <th>{t("bankStatement.error")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file, index) => {
                  const rowClass =
                    file.category === "empty"
                      ? classes.rowEmpty
                      : file.category === "ok"
                      ? classes.rowOk
                      : classes.rowError;
                  const iconClass =
                    file.category === "empty"
                      ? classes.statusIconEmpty
                      : file.category === "ok"
                      ? classes.statusIconOk
                      : classes.statusIconError;
                  const icon =
                    file.category === "empty" ? "!" : file.category === "ok" ? "✓" : "✕";

                  return (
                    <tr key={index} className={rowClass}>
                      <td className={classes.tdStatus}>
                        <span className={`${classes.statusIcon} ${iconClass}`}>
                          {icon}
                        </span>
                      </td>
                      <td className={classes.tdName}>{file.fileName}</td>
                      <td className={classes.tdType}>
                        {file.type || t("bankStatement.unknown")}
                      </td>
                      <td className={file.category === "empty" ? classes.tdNote : classes.tdError}>
                        {file.category === "ok" ? "—" : file.error || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredFiles.length === 0 && (
              <div className={classes.notFound}>{t("bankStatement.notFound")}</div>
            )}
          </div>
        )}
      </div>

      {/* Кнопка закрытия */}
      <div className={classes.closeButton}>
        <NButton onClick={onClose} styles={{ maxWidth: "200px", margin: "0 auto" }}>
          {t("buttons.Close")}
        </NButton>
      </div>
    </>
  );
};

export default UploadResultsView;